import { toast } from "sonner";
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

/** What the user is told, per surface. Defaults describe a whole page's data. */
export interface RefreshMessages {
  /** Shown as the refresh starts. Present tense — nothing has finished yet. */
  inProgress?: string;
  /** Shown once it succeeds. */
  complete?: string;
}

/**
 * Runs a refresh and tells the user how it went.
 *
 * Extracted because six surfaces carried this same announce → run → announce tail, three of
 * them character for character, which jscpd flagged as a clone the moment FIN-459's toast
 * migration touched them. The wording is the only thing that legitimately varies, so that is
 * the only thing callers pass.
 *
 * The in-progress toast is deliberately NEUTRAL rather than a success — nothing has settled
 * when it fires. The caller keeps ownership of its own `refreshing` flag: this returns
 * normally on failure (the error is reported, not rethrown), so a caller's `finally` clears
 * the flag either way and the button cannot get stuck.
 */
export async function runRefreshWithFeedback(
  refresh: () => Promise<void>,
  messages: RefreshMessages = {},
): Promise<void> {
  toast(translateOutsideReact('consultantToasts.refresh.inProgressTitle'), {
    description: messages.inProgress ?? translateOutsideReact('consultantToasts.refresh.defaultInProgress'),
  });
  try {
    await refresh();
    toast.success(translateOutsideReact('consultantToasts.refresh.completeTitle'), {
      description: messages.complete ?? translateOutsideReact('consultantToasts.refresh.defaultComplete'),
    });
  } catch (error) {
    console.error("Error refreshing data:", error);
    toast.error(translateOutsideReact('consultantToasts.refresh.failedTitle'), {
      description: translateOutsideReact('consultantToasts.refresh.failedBody'),
    });
  }
}
